import { useState, useEffect, useRef } from 'react';
import { testimonials } from '@/mocks/data';

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

const avatarColors = [
  'bg-primary-500',
  'bg-accent-500',
  'bg-secondary-500',
  'bg-primary-600',
  'bg-accent-600',
  'bg-secondary-600',
  'bg-primary-400',
  'bg-accent-400',
];

export default function TestimonialsSection() {
  const [visible, setVisible] = useState(false);
  const [page, setPage] = useState(0);
  const [cardsPerPage, setCardsPerPage] = useState(3);
  const [animateCards, setAnimateCards] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const update = () => {
      if (window.innerWidth < 768) setCardsPerPage(1);
      else if (window.innerWidth < 1024) setCardsPerPage(2);
      else setCardsPerPage(3);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const totalPages = Math.max(1, Math.ceil(testimonials.length / cardsPerPage));

  useEffect(() => {
    setPage(0);
  }, [cardsPerPage]);

  const goToPage = (newPage: number) => {
    setAnimateCards(false);
    setTimeout(() => {
      setPage(newPage);
      setAnimateCards(true);
    }, 150);
  };

  const nextPage = () => goToPage((page + 1) % totalPages);
  const prevPage = () => goToPage((page - 1 + totalPages) % totalPages);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          setAnimateCards(true);
        }
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const currentTestimonials = testimonials.slice(
    page * cardsPerPage,
    page * cardsPerPage + cardsPerPage
  );

  return (
    <section
      id="testimonials"
      ref={ref}
      className="relative py-24 md:py-32 bg-background-50"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          className={`flex flex-col items-center text-center mb-12 md:mb-16 transition-all duration-800 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Badge Google */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-background-300/60 bg-white/70 mb-6 hover:border-primary-500 hover:bg-primary-50 transition-all duration-300 cursor-default group">
            <span className="w-5 h-5 rounded-full bg-foreground-950 flex items-center justify-center text-[10px] font-bold text-background-50 group-hover:bg-primary-500 transition-colors duration-300">
              G
            </span>
            <span className="text-xs font-semibold tracking-[0.15em] uppercase text-foreground-600 group-hover:text-primary-600 transition-colors duration-300">
              RESEÑAS DE GOOGLE
            </span>
          </div>

          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 mb-6">
            Los testimonios de nuestros clientes son el reflejo de nuestro compromiso.
          </h2>

          {/* Rating badge */}
          <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full border border-background-300/60 bg-white/70 hover:border-primary-500 hover:bg-primary-50 transition-all duration-300 cursor-default group">
            <span className="text-foreground-950 font-bold text-lg group-hover:text-primary-600 transition-colors duration-300">5.0</span>
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
              ))}
            </div>

          </div>
        </div>

        {/* Carousel */}
        <div className="relative">
          {/* Left arrow */}
          <button
            onClick={prevPage}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-11 md:h-11 rounded-full border border-background-300/60 bg-white/80 hover:bg-background-200 flex items-center justify-center text-foreground-500 hover:text-foreground-950 transition-all duration-300 cursor-pointer"
            aria-label="Anterior"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>

          {/* Cards */}
          <div className="mx-10 md:mx-14">
            <div
              className={`grid gap-4 md:gap-5 transition-all duration-500 ease-out ${
                animateCards ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              } ${
                cardsPerPage === 1
                  ? 'grid-cols-1'
                  : cardsPerPage === 2
                  ? 'grid-cols-2'
                  : 'grid-cols-3'
              }`}
            >
              {currentTestimonials.map((t, idx) => {
                const isHovered = hoveredId === t.id;
                return (
                  <div
                    key={t.id}
                    onMouseEnter={() => setHoveredId(t.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    className={`group relative rounded-xl p-5 md:p-6 flex flex-col border transition-all duration-500 ease-out cursor-pointer ${
                      isHovered
                        ? 'bg-primary-500 border-primary-500 shadow-xl scale-[1.02]'
                        : 'bg-white border-background-200'
                    }`}
                  >
                    {/* Stars and G */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-0.5">
                        {[...Array(t.rating)].map((_, i) => (
                          <i
                            key={i}
                            className={`ri-star-fill text-sm transition-colors duration-300 ${isHovered ? 'text-white' : 'text-yellow-500'}`}
                          ></i>
                        ))}
                      </div>
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-colors duration-300 ${isHovered ? 'bg-white text-primary-500' : 'bg-foreground-950 text-background-50'}`}>
                        G
                      </span>
                    </div>

                    {/* Quote */}
                    <p className={`text-sm leading-relaxed flex-grow mb-5 transition-colors duration-300 ${isHovered ? 'text-white/90' : 'text-foreground-600'}`}>
                      &ldquo;{t.quote}&rdquo;
                    </p>

                    {/* Separator + Author */}
                    <div className={`border-t pt-4 flex items-center gap-3 transition-colors duration-300 ${isHovered ? 'border-white/30' : 'border-background-200'}`}>
                      <div
                        className={`w-9 h-9 rounded-full ${avatarColors[(page * cardsPerPage + idx) % avatarColors.length]} flex items-center justify-center text-xs font-bold text-white flex-shrink-0`}
                      >
                        {getInitials(t.author)}
                      </div>
                      <p className={`text-sm font-semibold transition-colors duration-300 ${isHovered ? 'text-white' : 'text-foreground-950'}`}>
                        {t.author}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right arrow */}
          <button
            onClick={nextPage}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-11 md:h-11 rounded-full border border-background-300/60 bg-white/80 hover:bg-background-200 flex items-center justify-center text-foreground-500 hover:text-foreground-950 transition-all duration-300 cursor-pointer"
            aria-label="Siguiente"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-2 mt-8">
          {[...Array(totalPages)].map((_, idx) => (
            <button
              key={idx}
              onClick={() => goToPage(idx)}
              className={`transition-all duration-300 rounded-full cursor-pointer ${
                idx === page
                  ? 'w-6 h-2 bg-foreground-950'
                  : 'w-2 h-2 bg-background-300 hover:bg-foreground-400'
              }`}
              aria-label={`Página ${idx + 1}`}
            />
          ))}
        </div>

        {/* Google link */}
        <div className="flex justify-center mt-10">
          <a
            href="https://share.google/8BRLneWkH7Tb4MD33"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full border border-background-300/60 bg-white/70 text-foreground-600 hover:text-foreground-950 hover:border-background-400 transition-all duration-300 text-sm font-medium"
          >
            <span className="w-5 h-5 rounded-full bg-foreground-950 flex items-center justify-center text-[10px] font-bold text-background-50">
              G
            </span>
            Ver todas las reseñas en Google
          </a>
        </div>
      </div>

      {/* Wave bottom - transition to Contact (primary-500) */}
      <div
        className="absolute bottom-0 left-0 w-full pointer-events-none overflow-hidden"
        style={{ height: '60px', marginBottom: '-1px' }}
      >
        <svg
          viewBox="0 0 1440 60"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          className="w-full h-full block"
          style={{ display: 'block' }}
        >
          <path
            d="M0 60H1440V30C1080 12 360 48 0 30V60Z"
            fill="oklch(0.27 0.065 190)"
            stroke="none"
          />
        </svg>
      </div>
    </section>
  );
}